import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload } from 'lucide-react';
import { useUploadStore } from '../../store/uploadStore';
import styles from './UploadPill.module.css';

export function UploadPill() {
  const tasks = useUploadStore((s) => s.tasks);
  const isUploading = useUploadStore((s) => s.isUploading);
  const selectionActive = useUploadStore((s) => s.selectionActive);
  const clearAll = useUploadStore((s) => s.clearAll);

  const done = tasks.filter((t) => t.status === 'done').length;
  const failed = tasks.filter((t) => t.status === 'failed').length;
  const total = tasks.length;
  const settled = total > 0 && done + failed === total;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  // Auto-dismiss the pill 4 seconds after all tasks settle
  useEffect(() => {
    if (!settled) return;
    const t = setTimeout(() => clearAll(), 4_000);
    return () => clearTimeout(t);
  }, [settled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (tasks.length === 0) return null;

  const handleClick = () => {
    window.dispatchEvent(new CustomEvent('upload:open-modal'));
  };

  return (
    <AnimatePresence>
      {tasks.length > 0 && (
        <motion.button
          className={styles['pill']}
          onClick={handleClick}
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1, bottom: selectionActive ? '5.5rem' : '1.5rem' }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 30 }}
          aria-label="View upload progress"
        >
          <div className={styles['pill-inner']}>
            <div className={styles['pill-left']}>
              {isUploading ? (
                <span className={styles['dot-uploading']} aria-hidden />
              ) : settled ? (
                <Upload className={styles['icon']} aria-hidden />
              ) : (
                <Upload className={styles['icon']} aria-hidden />
              )}
              <span className={styles['label']}>
                {isUploading
                  ? `Uploading ${done + 1} of ${total}`
                  : settled
                  ? `${done} uploaded${failed > 0 ? `, ${failed} failed` : ''}`
                  : `${total} queued`}
              </span>
            </div>
            {!settled && (
              <div className={styles['track']}>
                <motion.div
                  className={styles['fill']}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                />
              </div>
            )}
          </div>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
